import { CopyOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import { BlackSpan, GraySpan } from '../components/formSpans';

export type CardInfoProps = {
  label: string;
  data?: ReactNode;
  haveBorder?: boolean;
  truncate?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Gap between label and value. Number is px. Default 5px. */
  labelDataGap?: string | number;
  /** Allow long values (emails, paths) to wrap. */
  dataBreakWord?: boolean;
  onCopy?: () => void;
  copyTitle?: string;
};

const CardInfoRoot = styled.div<{ $haveBorder: boolean; $truncate: boolean }>`
  min-width: 0;
  ${(p) =>
    p.$haveBorder
      ? `
    border-left: 1px solid var(--color-primary, #41aaaf);
    padding-left: 10px;
  `
      : ''}
  ${(p) => (p.$truncate ? `overflow: hidden;` : '')}
`;

const Stack = styled.div<{ $gap: string }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap};
  min-width: 0;
`;

const LabelText = styled(GraySpan)`
  display: block;
`;

const ValueText = styled(BlackSpan)<{ $breakWord: boolean }>`
  display: block;
  min-width: 0;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

const HoverCopyButton = styled.button`
  position: absolute;
  right: -10px;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  cursor: pointer;
  color: var(--color-text-muted, #5a6a6a);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  background-color: transparent;
  border-radius: 4px;

  &:hover {
    background-color: var(--color-primary-soft, #e6f7f7);
    color: var(--color-text-muted, #5a6a6a);
  }
`;

const ValueWithHoverCopy = styled.div`
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;

  &:hover ${HoverCopyButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const CopyableValueButton = styled.button<{ $breakWord: boolean }>`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
  cursor: pointer;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

export const CardInfo = ({
  label,
  data,
  haveBorder = false,
  truncate = false,
  className,
  style,
  labelDataGap = 5,
  dataBreakWord = false,
  onCopy,
  copyTitle = 'Copy',
}: CardInfoProps) => {
  const gap =
    typeof labelDataGap === 'number' ? `${labelDataGap}px` : labelDataGap;

  const valueNode =
    onCopy != null ? (
      <ValueWithHoverCopy>
        <Tooltip title={copyTitle}>
          <CopyableValueButton
            type="button"
            onClick={onCopy}
            aria-label={copyTitle}
            $breakWord={dataBreakWord}
          >
            {data}
          </CopyableValueButton>
        </Tooltip>
        <Tooltip title={copyTitle}>
          <HoverCopyButton
            type="button"
            onClick={onCopy}
            aria-label={copyTitle}
          >
            <CopyOutlined style={{ fontSize: 18 }} />
          </HoverCopyButton>
        </Tooltip>
      </ValueWithHoverCopy>
    ) : (
      <ValueText $breakWord={dataBreakWord}>{data}</ValueText>
    );

  return (
    <CardInfoRoot
      className={className}
      style={style}
      $haveBorder={haveBorder}
      $truncate={truncate}
    >
      <Stack $gap={gap}>
        <LabelText>{label}</LabelText>
        {valueNode}
      </Stack>
    </CardInfoRoot>
  );
};
